import {
  resolveDynamicVariables,
  buildDynamicContext,
} from "@/lib/replaceDynamicValues";
import { isExternalHref } from "@/lib/externalLink";
import { replacePlatformPlaceholder } from "@/lib/jsonLd";
import styles from "./safe-html.module.css";

function unwrapDangerouslySetInnerHtmlJsx(html) {
  if (!html || typeof html !== "string") return "";
  const match = html.match(
    /^\s*<div[^>]*\bdangerouslySetInnerHTML\s*=\s*\{\{\s*__html:\s*`([\s\S]*)`\s*,?\s*\}\}\s*\/?>\s*(?:<\/div>)?\s*$/,
  );
  return match ? match[1] : html;
}

function wrapLeadingLeakedCss(html) {
  if (!html || typeof html !== "string") return "";
  const trimmed = html.trimStart();
  if (trimmed.startsWith("<")) return html;

  const firstTagIndex = trimmed.indexOf("<");
  const leading =
    firstTagIndex === -1 ? trimmed : trimmed.slice(0, firstTagIndex);
  const rest = firstTagIndex === -1 ? "" : trimmed.slice(firstTagIndex);

  // Only real CSS rules (`selector { prop: value }`), so plain text such as
  // "Use code {SAVE10}" isn't swallowed into a <style> tag.
  if (!/[^{}]+\{\s*[a-z-]+\s*:[^{}]*\}/i.test(leading)) return html;

  return `<style>${leading}</style>${rest}`;
}

function fixEscapedHtmlStructure(html) {
  if (!html || typeof html !== "string") return html;

  let res = html;

  // Check if html contains escaped HTML tags (e.g. &lt;table, &lt;tbody, &lt;tr, &lt;td, &lt;dt, &lt;dd, etc.)
  if (
    /&lt;\s*\/?\s*(?:table|thead|tbody|tfoot|tr|th|td|dt|dd|dl|div|p|span|h[1-6]|ul|ol|li|a|img|strong|em|b|i|u|s|br|hr|sub|sup|blockquote|code|pre)\b/i.test(
      res,
    )
  ) {
    res = res
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }

  // Fix non-breaking spaces used in place of regular spaces in opening tags e.g. <td&nbsp;style=...
  res = res.replace(/<([a-z0-9]+)(?:&nbsp;)+/gi, "<$1 ");

  // Remove <p> tags wrapping table structural elements
  res = res.replace(
    /<p[^>]*>(?:\s|&nbsp;)*(<\/?(?:tbody|thead|tfoot|tr|caption|colgroup|col)\b[^>]*>)(?:\s|&nbsp;)*<\/p>/gi,
    "$1",
  );
  res = res.replace(
    /<p[^>]*>(?:\s|&nbsp;)*(<\/?(?:td|th)\b[^>]*>)(?:\s|&nbsp;)*<\/p>/gi,
    "$1",
  );

  // Unwrap <p>...</p> inside <td>...</td> or <th>...</th>
  res = res.replace(
    /(<(?:td|th)\b[^>]*>)\s*<p[^>]*>((?:(?!<\/(?:td|th)>)[\s\S])*)<\/p>\s*(<\/(?:td|th)>)/gi,
    "$1$2$3",
  );

  // Clean up <p> tags wrapping <dt> and <dd>
  res = res.replace(
    /<p[^>]*>(?:\s|&nbsp;)*(<\/?(?:dt|dd|dl)\b[^>]*>)(?:\s|&nbsp;)*<\/p>/gi,
    "$1",
  );
  res = res.replace(
    /<p[^>]*>(?:\s|&nbsp;)*(<(?:dt|dd)\b[^>]*>[\s\S]*?<\/(?:dt|dd)>)(?:\s|&nbsp;)*<\/p>/gi,
    "$1",
  );

  // Remove empty <p></p> or <p>&nbsp;</p> between table rows/cells/dl
  res = res.replace(
    /(<\/(?:tr|td|th|tbody|thead|tfoot|table|dd)>)\s*<p[^>]*>(?:&nbsp;|\s)*<\/p>/gi,
    "$1",
  );
  res = res.replace(
    /<p[^>]*>(?:&nbsp;|\s)*<\/p>\s*(<(?:tr|td|th|tbody|thead|tfoot|table|dt)\b)/gi,
    "$1",
  );

  // If table parts (tbody/tr) exist without <table>, wrap them in <table>
  if (/<(?:tbody|thead|tfoot|tr)\b/i.test(res) && !/<table\b/i.test(res)) {
    res = res.replace(
      /((?:<tbody[\s\S]*?<\/tbody>|<tr[\s\S]*?<\/tr>)+)/gi,
      '<div class="table-responsive"><table class="rich-table">$1</table></div>',
    );
  }

  // If <dt>/<dd> exist without <dl>, wrap them in <dl>
  if (/<(?:dt|dd)\b/i.test(res) && !/<dl\b/i.test(res)) {
    res = res.replace(
      /((?:<(?:dt|dd)[\s\S]*?<\/(?:dt|dd)>\s*)+)/gi,
      '<dl class="rich-dl">$1</dl>',
    );
  }

  return res;
}

// Wide tables would otherwise push the page sideways on mobile.
function wrapTables(html) {
  if (!html || typeof html !== "string" || !/<table\b/i.test(html)) {
    return html;
  }
  return html.replace(
    /(<div[^>]*\btable-responsive\b[^>]*>\s*)?(<table\b[\s\S]*?<\/table>)/gi,
    (match, wrapper, table) =>
      wrapper ? match : `<div class="table-responsive">${table}</div>`,
  );
}

// Quill 2's getSemanticHTML() writes every space as &nbsp;, which stops text
// from wrapping. Turn them back into real spaces, keeping runs of spaces
// (all but the last stay &nbsp;) and a lone &nbsp; that is an element's only
// content, e.g. <p>&nbsp;</p>.
function restoreQuillSpaces(html) {
  if (!html || typeof html !== "string" || !html.includes("&nbsp;")) {
    return html;
  }
  return html.replace(/(?:&nbsp;)+/g, (run, offset, str) => {
    if (str[offset - 1] === ">" && str[offset + run.length] === "<") {
      return run;
    }
    return `${run.slice(6)} `;
  });
}

// Quill 2's getSemanticHTML() saves a video embed as <a href="url">url</a>.
// Turn YouTube/Vimeo ones back into players.
function restoreQuillVideos(html) {
  if (!html || typeof html !== "string" || !html.includes("<a")) return html;
  return html.replace(
    /<a href="(https:\/\/(?:www\.)?(?:youtube(?:-nocookie)?\.com\/embed\/|player\.vimeo\.com\/video\/)[^"<>\s]+)">\1<\/a>/g,
    '<iframe class="ql-video" src="$1" frameborder="0" allowfullscreen="true"></iframe>',
  );
}

// Off-site links typed into rich text get nofollow, the same as every other
// external link on the site; any rel the editor already set is kept.
function nofollowExternalLinks(html) {
  if (!html || typeof html !== "string" || !html.includes("<a")) return html;
  return html.replace(/<a\b[^>]*>/gi, (tag) => {
    const href = tag.match(/\bhref\s*=\s*(["'])(.*?)\1/i)?.[2];
    if (!isExternalHref(href)) return tag;
    const rel = tag.match(/\brel\s*=\s*(["'])(.*?)\1/i);
    if (!rel) return tag.replace(/^<a\b/i, '<a rel="nofollow"');
    if (/\bnofollow\b/i.test(rel[2])) return tag;
    return tag.replace(rel[0], `rel="${`${rel[2]} nofollow`.trim()}"`);
  });
}

function demoteH1Headings(html) {
  if (!html || typeof html !== "string" || !html.includes("<h1")) return html;
  return html
    .replace(/<h1(\b[^>]*)>/gi, "<h2$1>")
    .replace(/<\/h1>/gi, "</h2>");
}

function normalizeHtml(html, demoteH1 = false) {
  const normalized = wrapTables(
    nofollowExternalLinks(
      restoreQuillVideos(
        restoreQuillSpaces(
          wrapLeadingLeakedCss(
            unwrapDangerouslySetInnerHtmlJsx(fixEscapedHtmlStructure(html)),
          ),
        ),
      ),
    ),
  );
  // An unresolved {{platform_name}} placeholder in CMS copy reads "Tykkit".
  const withPlatform = replacePlatformPlaceholder(normalized);
  return demoteH1 ? demoteH1Headings(withPlatform) : withPlatform;
}

// Rich-text titles arrive as "<p>Beyond<br>Ticket Sales</p>". Headings only
// take inline markup, so paragraph breaks become <br> and the <p>s go.
function unwrapParagraphs(html) {
  return html
    .replace(/<\/p>\s*<p[^>]*>/gi, "<br>")
    .replace(/<\/?p[^>]*>/gi, "")
    .trim();
}

const hasVisibleText = (html) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;

// `as` renders the wrapper as another element, e.g. a heading, so CMS
// titles keep their semantic tag (a <div> isn't allowed inside an <h1>).
// `inline` unwraps paragraphs for that case and renders nothing when the
// editor left only an empty line.
export default function SafeHtml({
  html,
  className = "",
  style,
  context,
  as: Tag = "div",
  inline = false,
  demoteH1 = false,
}) {
  if (!html) return null;

  let content = html;
  if (inline && typeof content === "string") {
    content = unwrapParagraphs(content);
    if (!hasVisibleText(content)) return null;
  }
  if (typeof content === "string" && content.includes("{{")) {
    content = resolveDynamicVariables(
      content,
      context || buildDynamicContext(),
    );
  }

  return (
    <Tag
      className={`${styles.safeHtml} ${className}`}
      style={style}
      // CMS HTML / dynamic FAQ tokens can diverge slightly across SSR↔client;
      // suppress so a markup-only mismatch does not block booking UI.
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: normalizeHtml(content, demoteH1) }}
    />
  );
}
